// Language Context for React Components
// =====================================

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import translationService, { type Language } from '../services/translationService';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
  availableLanguages: { code: Language; name: string; nativeName: string }[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(translationService.getCurrentLanguage());

  // Initialize language on mount
  useEffect(() => {
    translationService.initializeLanguage();
    setLanguageState(translationService.getCurrentLanguage());
  }, []);

  const setLanguage = (newLanguage: Language) => {
    translationService.setLanguage(newLanguage);
    setLanguageState(newLanguage);
  };

  const t = (key: string) => {
    return translationService.t(key as any);
  };

  const availableLanguages = translationService.getAvailableLanguages();

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    availableLanguages,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Higher-order component for easy language access
export const withLanguage = <P extends object>(
  Component: React.ComponentType<P & { t: (key: string) => string; language: Language }>
) => {
  return (props: P) => {
    const { t, language } = useLanguage();
    return <Component {...props} t={t} language={language} />;
  };
};

export default LanguageContext;
